import { Button, FormLabel, InfoPopover, Typography } from '@automata/ui';
import { OptimisationStageInput } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { FC, useMemo, useState } from 'react';

import { NumberBadge } from './NumberBadge';
import { OptimisationStageModal } from '../Planning/Optimisation';
import { STAGE_TYPE_OPTIONS } from '../Planning/Optimisation/constants';
import { ReorderableDataGrid } from '../ReorderableDataGrid';

type OptimizationStage = OptimisationStageInput & {
  id: string;
  name: string;
  __reorder__: string;
};

type OptimizationStagesListProps = {
  stages: OptimizationStage[];
  onReorderStages: (stages: OptimizationStage[]) => void;
  onAddStage: (stage: OptimisationStageInput) => void;
  onEditStage: (id: string, stage: OptimisationStageInput) => void;
  onDeleteStage: (id: string) => void;
};

const getMaxComputationTimeDisplay = (
  stopAt: OptimisationStageInput['stop_at']
): string => {
  if (stopAt?.max_computation_time) {
    const totalSeconds = stopAt.max_computation_time;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return 'No limit';
};

const StageTypesPopover = (
  <div>
    <p>
      The solver iteratively improves the metrics defined for each stage until
      its stopping conditions are met. It then progresses to the next stage,
      applying the configured carry-over rules for metrics from previous stages.
    </p>
    <ul>
      <li>
        <b>Makespan:</b> Set the target maximum run duration for the entire
        workflow.
      </li>
      <li>
        <b>Slack:</b> Adds buffer time around strict time constraints, to make
        the workflow more resilient to delays and interruptions.
      </li>
      <li>
        <b>Consistency:</b> Reduce variation in planned task timings across
        batches.
      </li>
      <li>
        <b>Deadtime:</b> Minimise the time labware spends idle at locations
        without an active task.
      </li>
      <li>
        <b>Timing targets:</b> Minimise deviation from the ideal times for each
        timing target.
      </li>
    </ul>
  </div>
);

export const OptimizationStagesList: FC<OptimizationStagesListProps> = ({
  stages,
  onReorderStages,
  onAddStage,
  onEditStage,
  onDeleteStage,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStage, setEditingStage] = useState<OptimizationStage | null>(
    null
  );
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const stageTypeLabels = useMemo(
    () =>
      new Map(
        STAGE_TYPE_OPTIONS.map(({ value, label }) => [value, label] as const)
      ),
    []
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Stage',
        flex: 1,
        renderCell: (params) => {
          const stageIndex = stages.findIndex(
            (stage) => stage.id === params.row.id
          );
          const stageNumber = stageIndex + 1;
          return (
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  #{stageNumber}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {stageTypeLabels.get(params.row.objective ?? 'makespan') ??
                    'Makespan'}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {getMaxComputationTimeDisplay(params.row.stop_at)}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={() => handleEditStage(params.row)}
            aria-label={`Edit ${params.row.name}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [stageTypeLabels, stages]
  );

  const handleAddStage = () => {
    setModalMode('add');
    setEditingStage(null);
    setModalOpen(true);
  };

  const handleEditStage = (stage: OptimizationStage) => {
    setModalMode('edit');
    setEditingStage(stage);
    setModalOpen(true);
  };

  const handleSaveStage = (stageData: OptimisationStageInput) => {
    if (modalMode === 'add') {
      onAddStage(stageData);
    } else if (editingStage) {
      onEditStage(editingStage.id, stageData);
    }
    setModalOpen(false);
    setEditingStage(null);
  };

  const handleDeleteFromModal = () => {
    if (editingStage) {
      onDeleteStage(editingStage.id);
      setModalOpen(false);
      setEditingStage(null);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" gap={1} alignItems="center">
        <FormLabel
          infoPopover={
            <InfoPopover
              title="Optimisation Stage Types"
              copy={StageTypesPopover}
            />
          }
        >
          <NumberBadge value={2} />
          <Typography
            variant="body1"
            color="textSecondary"
            textTransform={'none'}
          >
            Optimization stages
          </Typography>
        </FormLabel>
      </Stack>

      <Typography variant="overline">stages</Typography>

      <ReorderableDataGrid
        data={stages}
        columns={columns}
        onReorder={onReorderStages}
        rowHeight={60}
        emptyStateMessage="No optimization stages configured. Add your first stage to get started."
      />

      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAddStage}
      >
        Add Stage
      </Button>

      <OptimisationStageModal
        open={modalOpen}
        mode={modalMode}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveStage}
        onDelete={modalMode === 'edit' ? handleDeleteFromModal : undefined}
        initialData={editingStage ?? undefined}
      />
    </Stack>
  );
};
